// third-party
import { FormattedMessage } from 'react-intl';

// project-imports
import { handlerCustomerDialog } from '@/api/customer';
import { useGetMenu } from '@/api/menu';
import { NavActionType } from '@/config';

// assets
import {
  Add,
  Link1,
  KyberNetwork,
  Messages2,
  Calendar1,
  Kanban,
  Profile2User,
  Bill,
  UserSquare,
  ShoppingBag,
  Graph,
  Chart21,
  Map1,
  Refresh,
  Home3,
  HomeTrendUp,
  Box1,
  Book,
  PasswordCheck,
  Next,
  RowVertical,
  CpuCharge,
  TableDocument,
  Subtitle,
  Book1,
  I24Support,
  Security,
  MessageProgramming,
  DollarSquare,
  Airplane,
  DocumentCode2,
  OceanProtocol,
  Level,
  ShieldCross,
  InfoCircle,
  Driving,
  Story,
  Fatrows,
  PresentionChart
} from 'iconsax-react';

// type
import { NavItemType } from '@/types/bs-types/menu';

// icons
const icons = {
  applications: KyberNetwork,
  chat: Messages2,
  calendar: Calendar1,
  kanban: Kanban,
  customer: Profile2User,
  invoice: Bill,
  profile: UserSquare,
  ecommerce: ShoppingBag,
  add: Add,
  link: Link1,
  charts: Chart21,
  chart: Graph,
  map: Map1,
  navigation: Home3,
  dashboard: HomeTrendUp,
  components: Box1,
  loading: Refresh,
  formsTable: Book,
  validation: PasswordCheck,
  wizard: Next,
  layout: RowVertical,
  plugins: CpuCharge,
  reactTables: TableDocument,
  muiTables: Subtitle,
  page: Book1,
  authentication: Security,
  maintenance: MessageProgramming,
  pricing: DollarSquare,
  contactus: I24Support,
  landing: Airplane,
  samplePage: DocumentCode2,
  menuLevel: OceanProtocol,
  menuLevelSubtitle: Level,
  disabledMenu: ShieldCross,
  chipMenu: InfoCircle,
  documentation: I24Support,
  roadmap: Driving,
  widgets: Story,
  statistics: Story,
  data: Fatrows,
  chart2: PresentionChart
};

const rootPath = '/example';

const applications: NavItemType = {
  id: 'group-applications',
  title: <FormattedMessage id="applications" />,
  icon: icons.applications,
  type: 'group',
  children: [
    {
      id: 'chat',
      title: <FormattedMessage id="chat" />,
      type: 'item',
      url: `${rootPath}/apps/chat`,
      icon: icons.chat,
      breadcrumbs: false
    },
    {
      id: 'calendar',
      title: <FormattedMessage id="calendar" />,
      type: 'item',
      url: `${rootPath}/apps/calendar`,
      icon: icons.calendar,
      actions: [
        {
          type: NavActionType.LINK,
          label: 'Full Calendar',
          icon: icons.link,
          url: 'https://fullcalendar.io/docs/react',
          target: true
        }
      ]
    },
    {
      id: 'kanban',
      title: <FormattedMessage id="kanban" />,
      type: 'item',
      icon: icons.kanban,
      url: `${rootPath}/apps/kanban/board`,
      link: `${rootPath}/apps/kanban/:tab`,
      breadcrumbs: false
    },
    {
      id: 'customer',
      title: <FormattedMessage id="customer" />,
      type: 'collapse',
      icon: icons.customer,
      children: [
        {
          id: 'customer-list',
          title: <FormattedMessage id="list" />,
          type: 'item',
          url: `${rootPath}/apps/customer/customer-list`,
          actions: [
            {
              type: NavActionType.FUNCTION,
              label: 'Add Customer',
              function: () => handlerCustomerDialog(true),
              icon: icons.add
            }
          ]
        },
        {
          id: 'customer-card',
          title: <FormattedMessage id="cards" />,
          type: 'item',
          url: `${rootPath}/apps/customer/customer-card`
        }
      ]
    },
    {
      id: 'invoice',
      title: <FormattedMessage id="invoice" />,
      url: `${rootPath}/apps/invoice/dashboard`,
      type: 'collapse',
      icon: icons.invoice,
      breadcrumbs: false,
      children: [
        {
          id: 'create',
          title: <FormattedMessage id="create" />,
          type: 'item',
          url: `${rootPath}/apps/invoice/create`,
          breadcrumbs: false
        },
        {
          id: 'details',
          title: <FormattedMessage id="details" />,
          type: 'item',
          url: `${rootPath}/apps/invoice/details/1`,
          link: `${rootPath}/apps/invoice/details/:id`,
          breadcrumbs: false
        },
        {
          id: 'list',
          title: <FormattedMessage id="list" />,
          type: 'item',
          url: `${rootPath}/apps/invoice/list`,
          breadcrumbs: false
        },
        {
          id: 'edit',
          title: <FormattedMessage id="edit" />,
          type: 'item',
          url: `${rootPath}/apps/invoice/edit/1`,
          link: `${rootPath}/apps/invoice/edit/:id'`,
          breadcrumbs: false
        }
      ]
    },
    {
      id: 'profile',
      title: <FormattedMessage id="profile" />,
      type: 'collapse',
      icon: icons.profile,
      children: [
        {
          id: 'user-profile',
          title: <FormattedMessage id="user-profile" />,
          type: 'item',
          link: `${rootPath}/apps/profiles/user/:tab`,
          url: `${rootPath}/apps/profiles/user/personal`,
          breadcrumbs: false
        },
        {
          id: 'account-profile',
          title: <FormattedMessage id="account-profile" />,
          type: 'item',
          url: `${rootPath}/apps/profiles/account/basic`,
          link: `${rootPath}/apps/profiles/account/:tab`,
          breadcrumbs: false
        }
      ]
    },

    {
      id: 'e-commerce',
      title: <FormattedMessage id="e-commerce" />,
      type: 'collapse',
      icon: icons.ecommerce,
      children: [
        {
          id: 'products',
          title: <FormattedMessage id="products" />,
          type: 'item',
          url: `${rootPath}/apps/e-commerce/products`
        },
        {
          id: 'product-details',
          title: <FormattedMessage id="product-details" />,
          type: 'item',
          link: `${rootPath}/apps/e-commerce/product-details/:id`,
          url: `${rootPath}/apps/e-commerce/product-details/1`,
          breadcrumbs: false
        },
        {
          id: 'product-list',
          title: <FormattedMessage id="product-list" />,
          type: 'item',
          url: `${rootPath}/apps/e-commerce/product-list`,
          breadcrumbs: false
        },
        {
          id: 'add-new-product',
          title: <FormattedMessage id="add-new-product" />,
          type: 'item',
          url: `${rootPath}/apps/e-commerce/add-new-product`
        },
        {
          id: 'checkout',
          title: <FormattedMessage id="checkout" />,
          type: 'item',
          url: `${rootPath}/apps/e-commerce/checkout`
        }
      ]
    }
  ]
};

const chartsMap: NavItemType = {
  id: 'group-charts-map',
  title: <FormattedMessage id="charts-map" />,
  icon: icons.charts,
  type: 'group',
  children: [
    {
      id: 'react-chart',
      title: <FormattedMessage id="charts" />,
      type: 'collapse',
      icon: icons.chart,
      children: [
        {
          id: 'apexchart',
          title: <FormattedMessage id="apexchart" />,
          type: 'item',
          url: `${rootPath}/charts/apexchart`
        },
        {
          id: 'org-chart',
          title: <FormattedMessage id="org-chart" />,
          type: 'item',
          url: `${rootPath}/charts/org-chart`
        }
      ]
    }
    // {
    //   id: 'map',
    //   title: <FormattedMessage id="map" />,
    //   type: 'item',
    //   url: `${rootPath}/map`,
    //   icon: icons.map
    // }
  ]
};

const loadingMenu: NavItemType = {
  id: 'group-dashboard-loading',
  title: <FormattedMessage id="dashboard" />,
  type: 'group',
  icon: icons.loading,
  children: [
    {
      id: 'dashboard1',
      title: <FormattedMessage id="dashboard" />,
      type: 'collapse',
      icon: icons.loading,
      children: [
        {
          id: 'default1',
          title: 'Default',
          type: 'item',
          url: `${rootPath}/dashboard/default`,
          breadcrumbs: false
        },
        {
          id: 'analytics1',
          title: 'Analytics',
          type: 'item',
          url: `${rootPath}/dashboard/analytics`,
          breadcrumbs: false
        }
      ]
    }
  ]
};

const formsTables: NavItemType = {
  id: 'group-forms-tables',
  title: <FormattedMessage id="forms-tables" />,
  icon: icons.formsTable,
  type: 'group',
  children: [
    {
      id: 'validation',
      title: <FormattedMessage id="forms-validation" />,
      type: 'item',
      url: `${rootPath}/forms/validation`,
      icon: icons.validation
    },
    {
      id: 'wizard',
      title: <FormattedMessage id="forms-wizard" />,
      type: 'item',
      url: `${rootPath}/forms/wizard`,
      icon: icons.wizard
    },
    {
      id: 'forms-layout',
      title: <FormattedMessage id="layout" />,
      type: 'collapse',
      icon: icons.layout,
      children: [
        {
          id: 'basic',
          title: <FormattedMessage id="basic" />,
          type: 'item',
          url: `${rootPath}/forms/layout/basic`
        },
        {
          id: 'multi-column',
          title: <FormattedMessage id="multi-column" />,
          type: 'item',
          url: `${rootPath}/forms/layout/multi-column`
        },
        {
          id: 'action-bar',
          title: <FormattedMessage id="action-bar" />,
          type: 'item',
          url: `${rootPath}/forms/layout/action-bar`
        },
        {
          id: 'sticky-bar',
          title: <FormattedMessage id="sticky-bar" />,
          type: 'item',
          url: `${rootPath}/forms/layout/sticky-bar`
        }
      ]
    },
    {
      id: 'forms-plugins',
      title: <FormattedMessage id="plugins" />,
      type: 'collapse',
      icon: icons.plugins,
      children: [
        {
          id: 'mask',
          title: <FormattedMessage id="mask" />,
          type: 'item',
          url: `${rootPath}/forms/plugins/mask`
        },
        {
          id: 'clipboard',
          title: <FormattedMessage id="clipboard" />,
          type: 'item',
          url: `${rootPath}/forms/plugins/clipboard`
        },
        {
          id: 're-captcha',
          title: <FormattedMessage id="re-captcha" />,
          type: 'item',
          url: `${rootPath}/forms/plugins/re-captcha`
        },
        {
          id: 'editor',
          title: <FormattedMessage id="editor" />,
          type: 'item',
          url: `${rootPath}/forms/plugins/editor`
        },
        {
          id: 'dropzone',
          title: <FormattedMessage id="dropzone" />,
          type: 'item',
          url: `${rootPath}/forms/plugins/dropzone`
        }
      ]
    },
    {
      id: 'react-tables',
      title: <FormattedMessage id="react-table" />,
      type: 'collapse',
      icon: icons.reactTables,
      children: [
        {
          id: 'rt-table',
          title: <FormattedMessage id="basic" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/basic`
        },
        {
          id: 'rt-dense',
          title: <FormattedMessage id="dense" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/dense`
        },
        {
          id: 'rt-sorting',
          title: <FormattedMessage id="sorting" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/sorting`
        },
        {
          id: 'rt-filtering',
          title: <FormattedMessage id="filtering" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/filtering`
        },
        {
          id: 'rt-grouping',
          title: <FormattedMessage id="grouping" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/grouping`
        },
        {
          id: 'rt-pagination',
          title: <FormattedMessage id="pagination" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/pagination`
        },
        {
          id: 'rt-row-selection',
          title: <FormattedMessage id="row-selection" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/row-selection`
        },
        {
          id: 'rt-expanding',
          title: <FormattedMessage id="expanding" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/expanding`
        },
        {
          id: 'rt-editable',
          title: <FormattedMessage id="editable" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/editable`
        },
        {
          id: 'rt-drag-drop',
          title: <FormattedMessage id="drag-drop" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/drag-drop`
        },
        {
          id: 'rt-column-visibility',
          title: <FormattedMessage id="column-visibility" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/column-visibility`
        },
        {
          id: 'rt-column-resizing',
          title: <FormattedMessage id="column-resizing" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/column-resizing`
        },
        {
          id: 'rt-sticky-table',
          title: <FormattedMessage id="sticky" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/sticky-table`
        },
        {
          id: 'rt-umbrella',
          title: <FormattedMessage id="umbrella" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/umbrella`
        },
        {
          id: 'rt-empty',
          title: <FormattedMessage id="empty" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/empty`
        },
        {
          id: 'rt-virtualized',
          title: <FormattedMessage id="virtualized" />,
          type: 'item',
          url: `${rootPath}/tables/react-table/virtualized`
        }
      ]
    },
    {
      id: 'mui-tables',
      title: <FormattedMessage id="mui-table" />,
      type: 'collapse',
      icon: icons.muiTables,
      children: [
        {
          id: 'mui-table',
          title: <FormattedMessage id="basic" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/basic`
        },
        {
          id: 'mui-dense',
          title: <FormattedMessage id="dense" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/dense`
        },
        {
          id: 'mui-enhanced',
          title: <FormattedMessage id="enhanced" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/enhanced`
        },
        {
          id: 'mui-data-table',
          title: <FormattedMessage id="datatable" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/datatable`
        },
        {
          id: 'mui-custom',
          title: <FormattedMessage id="custom" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/custom`
        },
        {
          id: 'mui-fixed-header',
          title: <FormattedMessage id="fixed-header" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/fixed-header`
        },
        {
          id: 'mui-collapse',
          title: <FormattedMessage id="collapse" />,
          type: 'item',
          url: `${rootPath}/tables/mui-table/collapse`
        }
      ]
    }
  ]
};

const pages: NavItemType = {
  id: 'group-pages',
  title: <FormattedMessage id="pages" />,
  type: 'group',
  icon: icons.page,
  children: [
    {
      id: 'authentication',
      title: <FormattedMessage id="authentication" />,
      type: 'collapse',
      icon: icons.authentication,
      children: [
        {
          id: 'authentication1',
          title: (
            <>
              <FormattedMessage id="authentication" /> 1
            </>
          ),
          type: 'collapse',
          children: [
            {
              id: 'login',
              title: <FormattedMessage id="login" />,
              type: 'item',
              url: `${rootPath}/auth/login`,
              target: true
            },
            {
              id: 'register',
              title: <FormattedMessage id="register" />,
              type: 'item',
              url: `${rootPath}/auth/register`,
              target: true
            },
            {
              id: 'forgot-password',
              title: <FormattedMessage id="forgot-password" />,
              type: 'item',
              url: `${rootPath}/auth/forgot-password`,
              target: true
            },
            {
              id: 'reset-password',
              title: <FormattedMessage id="reset-password" />,
              type: 'item',
              url: `${rootPath}/auth/reset-password`,
              target: true
            },
            {
              id: 'check-mail',
              title: <FormattedMessage id="check-mail" />,
              type: 'item',
              url: `${rootPath}/auth/check-mail`,
              target: true
            },
            {
              id: 'code-verification',
              title: <FormattedMessage id="code-verification" />,
              type: 'item',
              url: `${rootPath}/auth/code-verification`,
              target: true
            }
          ]
        },
        {
          id: 'authentication2',
          title: (
            <>
              <FormattedMessage id="authentication" /> 2
            </>
          ),
          type: 'collapse',
          children: [
            {
              id: 'login2',
              title: <FormattedMessage id="login" />,
              type: 'item',
              url: `${rootPath}/auth/login2`,
              target: true
            },
            {
              id: 'register2',
              title: <FormattedMessage id="register" />,
              type: 'item',
              url: `${rootPath}/auth/register2`,
              target: true
            },
            {
              id: 'forgot-password2',
              title: <FormattedMessage id="forgot-password" />,
              type: 'item',
              url: `${rootPath}/auth/forgot-password2`,
              target: true
            },
            {
              id: 'reset-password2',
              title: <FormattedMessage id="reset-password" />,
              type: 'item',
              url: `${rootPath}/auth/reset-password2`,
              target: true
            },
            {
              id: 'check-mail2',
              title: <FormattedMessage id="check-mail" />,
              type: 'item',
              url: `${rootPath}/auth/check-mail2`,
              target: true
            },
            {
              id: 'code-verification2',
              title: <FormattedMessage id="code-verification" />,
              type: 'item',
              url: `${rootPath}/auth/code-verification2`,
              target: true
            }
          ]
        },
        {
          id: 'authentication3',
          title: (
            <>
              <FormattedMessage id="authentication" /> 3
            </>
          ),
          type: 'item',
          url: `${rootPath}/auth/login3`,
          target: true
        }
      ]
    },
    {
      id: 'maintenance',
      title: <FormattedMessage id="maintenance" />,
      type: 'collapse',
      icon: icons.maintenance,
      isDropdown: true,
      children: [
        {
          id: 'error-404',
          title: <FormattedMessage id="error-404" />,
          type: 'item',
          url: `${rootPath}/maintenance/404`,
          target: true
        },
        {
          id: 'error-500',
          title: <FormattedMessage id="error-500" />,
          type: 'item',
          url: `${rootPath}/maintenance/500`,
          target: true
        },
        {
          id: 'coming-soon-collapse',
          title: <FormattedMessage id="coming-soon" />,
          type: 'collapse',
          children: [
            {
              id: 'coming-soon-1',
              title: (
                <>
                  <FormattedMessage id="coming-soon" /> 1
                </>
              ),
              type: 'item',
              url: `${rootPath}/maintenance/coming-soon`,
              target: true
            },
            {
              id: 'coming-soon-2',
              title: (
                <>
                  <FormattedMessage id="coming-soon" /> 2
                </>
              ),
              type: 'item',
              url: `${rootPath}/maintenance/coming-soon2`,
              target: true
            }
          ]
        },
        {
          id: 'under-construction-collapse',
          title: <FormattedMessage id="under-construction" />,
          type: 'collapse',
          children: [
            {
              id: 'under-construction-1',
              title: (
                <>
                  <FormattedMessage id="under-construction" /> 1
                </>
              ),
              type: 'item',
              url: `${rootPath}/maintenance/under-construction`,
              target: true
            },
            {
              id: 'under-construction-2',
              title: (
                <>
                  <FormattedMessage id="under-construction" /> 2
                </>
              ),
              type: 'item',
              url: `${rootPath}/maintenance/under-construction2`,
              target: true
            }
          ]
        }
      ]
    },
    {
      id: 'price',
      title: <FormattedMessage id="price" />,
      type: 'collapse',
      icon: icons.pricing,
      children: [
        {
          id: 'price1',
          title: (
            <>
              <FormattedMessage id="price" /> 1
            </>
          ),
          type: 'item',
          url: `${rootPath}/price/price1`
        },
        {
          id: 'price2',
          title: (
            <>
              <FormattedMessage id="price" /> 2
            </>
          ),
          type: 'item',
          url: `${rootPath}/price/price2`
        }
      ]
    },
    {
      id: 'contact-us',
      title: <FormattedMessage id="contact-us" />,
      type: 'item',
      url: `${rootPath}/contact-us`,
      icon: icons.contactus,
      target: true
    },
    {
      id: 'landing',
      title: <FormattedMessage id="landing" />,
      type: 'item',
      icon: icons.landing,
      url: `${rootPath}/landing`
    }
  ]
};

const samplePage: NavItemType = {
  id: 'sample-page',
  title: <FormattedMessage id="sample-page" />,
  type: 'group',
  url: `${rootPath}/sample-page`,
  icon: icons.samplePage
};

const flowFormPage: NavItemType = {
  id: 'flow-form-page',
  title: <FormattedMessage id="flow-form-page" />,
  type: 'group',
  children: [
    {
      id: 'form-page',
      title: <FormattedMessage id="form-page" />,
      type: 'item',
      icon: icons.samplePage,
      url: `${rootPath}/product-form`
    },
    {
      id: 'flow-page',
      title: <FormattedMessage id="flow-page" />,
      type: 'item',
      icon: icons.samplePage,
      url: `${rootPath}/product-flow`
    }
  ]
};

const support: NavItemType = {
  id: 'other',
  type: 'group',
  children: [
    {
      id: 'menu-level',
      title: <FormattedMessage id="menu-level" />,
      type: 'collapse',
      icon: icons.menuLevel,
      children: [
        {
          id: 'menu-level-1.1',
          title: (
            <>
              <FormattedMessage id="level" /> 1
            </>
          ),
          type: 'item',
          url: `${rootPath}#`
        },
        {
          id: 'menu-level-1.2',
          title: (
            <>
              <FormattedMessage id="level" /> 1
            </>
          ),
          type: 'collapse',
          children: [
            {
              id: 'menu-level-2.1',
              title: (
                <>
                  <FormattedMessage id="level" /> 2
                </>
              ),
              type: 'item',
              url: `${rootPath}#`
            },
            {
              id: 'menu-level-2.2',
              title: (
                <>
                  <FormattedMessage id="level" /> 2
                </>
              ),
              type: 'collapse',
              children: [
                {
                  id: 'menu-level-3.1',
                  title: (
                    <>
                      <FormattedMessage id="level" /> 3
                    </>
                  ),
                  type: 'item',
                  url: `${rootPath}#`
                },
                {
                  id: 'menu-level-3.2',
                  title: (
                    <>
                      <FormattedMessage id="level" /> 3
                    </>
                  ),
                  type: 'item',
                  url: `${rootPath}#`
                }
              ]
            }
          ]
        }
      ]
    },
    {
      id: 'menu-level-subtitle',
      title: <FormattedMessage id="menu-level-subtitle" />,
      caption: <FormattedMessage id="menu-level-subtitle-caption" />,
      type: 'collapse',
      icon: icons.menuLevelSubtitle,
      children: [
        {
          id: 'sub-menu-level-1.1',
          title: (
            <>
              <FormattedMessage id="level" /> 1
            </>
          ),
          caption: <FormattedMessage id="menu-level-subtitle-item" />,
          type: 'item',
          url: `${rootPath}#`
        },
        {
          id: 'sub-menu-level-1.2',
          title: (
            <>
              <FormattedMessage id="level" /> 1
            </>
          ),
          caption: <FormattedMessage id="menu-level-subtitle-collapse" />,
          type: 'collapse',
          children: [
            {
              id: 'sub-menu-level-2.1',
              title: (
                <>
                  <FormattedMessage id="level" /> 2
                </>
              ),
              caption: <FormattedMessage id="menu-level-subtitle-sub-item" />,
              type: 'item',
              url: `${rootPath}#`
            }
          ]
        }
      ]
    },
    {
      id: 'disabled-menu',
      title: <FormattedMessage id="disabled-menu" />,
      type: 'item',
      url: `${rootPath}#`,
      icon: icons.disabledMenu,
      disabled: true
    },
    {
      id: 'oval-chip-menu',
      title: <FormattedMessage id="oval-chip-menu" />,
      type: 'item',
      url: `${rootPath}#`,
      icon: icons.chipMenu,
      chip: {
        label: 'Fire',
        color: 'error',
        variant: 'outlined',
        size: 'small'
      }
    }
    // {
    //   id: 'documentation',
    //   title: <FormattedMessage id="documentation" />,
    //   type: 'item',
    //   url: 'https://phoenixcoded.gitbook.io/able-pro/v/react/',
    //   icon: icons.documentation,
    //   external: true,
    //   target: true,
    //   chip: {
    //     label: 'gitbook',
    //     color: 'info',
    //     size: 'small'
    //   }
    // },
    // {
    //   id: 'roadmap',
    //   title: <FormattedMessage id="roadmap" />,
    //   type: 'item',
    //   url: 'https://phoenixcoded.gitbook.io/able-pro/v/react/roadmap',
    //   icon: icons.roadmap,
    //   external: true,
    //   target: true
    // }
  ]
};

const widget: NavItemType = {
  id: 'group-widget',
  title: <FormattedMessage id="widgets" />,
  icon: icons.widgets,
  type: 'group',
  children: [
    {
      id: 'statistics',
      title: <FormattedMessage id="statistics" />,
      type: 'item',
      url: `${rootPath}/widget/statistics`,
      icon: icons.statistics
    },
    {
      id: 'data',
      title: <FormattedMessage id="data" />,
      type: 'item',
      url: `${rootPath}/widget/data`,
      icon: icons.data
    },
    {
      id: 'chart',
      title: <FormattedMessage id="chart" />,
      type: 'item',
      url: `${rootPath}/widget/chart`,
      icon: icons.chart2
    }
  ]
};

const toComponent: NavItemType = {
  id: 'group-component',
  title: <FormattedMessage id="component" />,
  icon: icons.widgets,
  type: 'group',
  children: [
    {
      id: 'component',
      title: <FormattedMessage id="component" />,
      type: 'item',
      url: `/components-overview`,
      icon: icons.chart2
    }
  ]
};

// ==============================|| MENU ITEMS - API ||============================== //

export { applications, chartsMap, loadingMenu, formsTables, pages, samplePage, flowFormPage, support, widget, toComponent };
